import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';

// ── Pipeline Types ──

export type PipelineStatus = 'draft' | 'ready' | 'running' | 'paused' | 'completed' | 'failed';
export type StepType = 'ingest' | 'transform' | 'enrich' | 'analyze' | 'export' | 'notify';

export interface PipelineStep {
  name: string;
  step_type: StepType;
  config: Record<string, unknown>;
  depends_on: string[];
  retry_count: number;
  timeout_seconds: number;
}

export interface StepStatus {
  name: string;
  step_type: StepType;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
  output: Record<string, unknown>;
}

export interface Pipeline {
  id: string;
  name: string;
  description: string;
  steps: PipelineStep[];
  status: PipelineStatus;
  schedule_cron: string | null;
  tags: string[];
  step_statuses: StepStatus[];
  progress: number;
  created_by: string;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
  last_error: string | null;
}

export interface PipelineListResponse {
  items: Pipeline[];
  total: number;
  page: number;
  page_size: number;
}

export interface PipelineCreateRequest {
  name: string;
  description: string;
  steps: PipelineStep[];
  schedule_cron?: string;
  tags?: string[];
}

// ── Agent Types ──

export type AgentStatus = 'pending' | 'active' | 'paused' | 'completed' | 'failed';
export type AgentType = 'investigator' | 'explorer' | 'hypothesis' | 'insight' | 'monitor';

export interface AgentAction {
  step: number;
  action: string;
  target: string | null;
  result_summary: string | null;
  entities_found: number;
  relationships_found: number;
  timestamp: string;
}

export interface AgentInvestigation {
  id: string;
  agent_type: AgentType;
  status: AgentStatus;
  target: string | null;
  objective: string | null;
  actions: AgentAction[];
  entities_found: number;
  relationships_found: number;
  hypotheses: Array<{ id: string; statement: string; confidence: number; evidence: unknown[]; category: string | null }>;
  insights: Array<{ id: string; title: string; description: string; category: string; severity: string; confidence: number }>;
  created_at: string;
  completed_at: string | null;
  progress: number;
}

export interface DailyBrief {
  id: string;
  date: string;
  summary: string;
  key_findings: Array<Record<string, unknown>>;
  new_entities: number;
  new_relationships: number;
  active_alerts: number;
  top_risks: Array<Record<string, unknown>>;
  recommendations: string[];
  created_at: string;
}

// ── OSINT Advanced Types ──

export interface ScrapeResult {
  url: string;
  title: string;
  content: string;
  entities: Array<{ text: string; type: string; confidence: number }>;
  links: string[];
  metadata: Record<string, unknown>;
}

export interface ThreatAlert {
  id: string;
  rule_name: string;
  severity: string;
  matched_text: string;
  source: string;
  timestamp: string;
}

// ── Dark Web Types ──

export interface DarkWebResult {
  source_type: string;
  title: string;
  content: string;
  url: string;
  date: string;
  relevance_score: number;
  threat_level: string;
}

export interface LeakCheckResult {
  identifier: string;
  breaches: Array<{ name: string; date: string; data_types: string[]; severity: string }>;
  total_breaches: number;
}

// ── API Functions — Pipelines ──

async function fetchPipelines(params?: { status?: PipelineStatus; page?: number; page_size?: number }): Promise<PipelineListResponse> {
  const { data } = await apiClient.get('/pipelines', { params });
  return data;
}

async function fetchPipeline(id: string): Promise<Pipeline> {
  const { data } = await apiClient.get(`/pipelines/${id}`);
  return data;
}

async function createPipeline(body: PipelineCreateRequest): Promise<Pipeline> {
  const { data } = await apiClient.post('/pipelines', body);
  return data;
}

async function executePipeline(id: string, params?: { parameters?: Record<string, unknown>; dry_run?: boolean }): Promise<Pipeline> {
  const { data } = await apiClient.post(`/pipelines/${id}/execute`, params ?? {});
  return data;
}

async function deletePipeline(id: string): Promise<void> {
  await apiClient.delete(`/pipelines/${id}`);
}

// ── API Functions — Agents ──

async function launchInvestigation(body: {
  target: string;
  objective?: string;
  max_steps?: number;
  depth?: number;
  modules?: string[];
  auto_correlate?: boolean;
}): Promise<AgentInvestigation> {
  const { data } = await apiClient.post('/agents/investigate', body);
  return data;
}

async function launchExploration(body: {
  entity_id: string;
  strategy?: string;
  max_hops?: number;
  max_nodes?: number;
}): Promise<AgentInvestigation> {
  const { data } = await apiClient.post('/agents/explore', body);
  return data;
}

async function generateHypotheses(body: {
  entity_id: string;
  context?: string;
  max_hypotheses?: number;
}): Promise<AgentInvestigation> {
  const { data } = await apiClient.post('/agents/hypotheses', body);
  return data;
}

async function generateInsights(body: {
  entity_ids?: string[];
  time_window_hours?: number;
  categories?: string[];
}): Promise<AgentInvestigation> {
  const { data } = await apiClient.post('/agents/insights', body);
  return data;
}

async function fetchAgentInvestigation(id: string): Promise<AgentInvestigation> {
  const { data } = await apiClient.get(`/agents/investigations/${id}`);
  return data;
}

async function fetchDailyBrief(): Promise<DailyBrief> {
  const { data } = await apiClient.get('/agents/daily-brief');
  return data;
}

// ── API Functions — OSINT Advanced ──

async function osintScrape(body: { url: string; extract_entities?: boolean }): Promise<ScrapeResult> {
  const { data } = await apiClient.post('/osint-advanced/scrape', body);
  return data;
}

async function osintCrawl(body: { url: string; max_depth?: number; max_pages?: number }): Promise<{ pages: ScrapeResult[]; total_pages: number }> {
  const { data } = await apiClient.post('/osint-advanced/crawl', body);
  return data;
}

async function osintExtractEntities(body: { text?: string; url?: string }): Promise<{ entities: Array<{ text: string; type: string; confidence: number }> }> {
  const { data } = await apiClient.post('/osint-advanced/extract-entities', body);
  return data;
}

async function osintThreats(): Promise<ThreatAlert[]> {
  const { data } = await apiClient.get('/osint-advanced/threats');
  return data.alerts ?? data;
}

async function osintThreatScan(body: { text?: string; url?: string }): Promise<{ threats: ThreatAlert[]; risk_level: string }> {
  const { data } = await apiClient.post('/osint-advanced/threats/scan', body);
  return data;
}

async function osintAgentInvestigatePerson(body: { name: string; context?: string }): Promise<Record<string, unknown>> {
  const { data } = await apiClient.post('/osint-advanced/agent/investigate-person', body);
  return data;
}

async function osintAgentInvestigateTopic(body: { topic: string; max_sources?: number }): Promise<Record<string, unknown>> {
  const { data } = await apiClient.post('/osint-advanced/agent/investigate-topic', body);
  return data;
}

async function osintAgentSearch(query: string): Promise<{ results: Array<Record<string, unknown>>; total: number }> {
  const { data } = await apiClient.get('/osint-advanced/agent/search', { params: { query } });
  return data;
}

async function osintAgentStats(): Promise<Record<string, unknown>> {
  const { data } = await apiClient.get('/osint-advanced/agent/stats');
  return data;
}

// ── API Functions — Dark Web ──

async function darkwebSearch(body: { query: string; source_types?: string[]; limit?: number }): Promise<{ results: DarkWebResult[]; total: number }> {
  const { data } = await apiClient.post('/darkweb/search', body);
  return data;
}

async function darkwebLeakCheck(body: { identifiers: string[]; check_types?: string[] }): Promise<{ results: LeakCheckResult[] }> {
  const { data } = await apiClient.post('/darkweb/leaks/check', body);
  return data;
}

async function darkwebAlerts(): Promise<{ alerts: Array<Record<string, unknown>> }> {
  const { data } = await apiClient.get('/darkweb/alerts');
  return data;
}

// ── API Functions — Correlation ──

async function runCorrelation(body: { method: string; entity_ids?: string[]; config?: Record<string, unknown> }): Promise<{ correlation_id: string; status: string }> {
  const { data } = await apiClient.post('/correlation/run', body);
  return data;
}

async function temporalCorrelation(body: { entity_ids: string[]; time_window?: string; granularity?: string }): Promise<Record<string, unknown>> {
  const { data } = await apiClient.post('/correlation/temporal', body);
  return data;
}

async function linkPrediction(body: { entity_ids?: string[]; methods?: string[] }): Promise<{ predictions: Array<Record<string, unknown>> }> {
  const { data } = await apiClient.post('/correlation/link-prediction', body);
  return data;
}

// ── React Query Hooks — Pipelines ──

export function usePipelines(params?: { status?: PipelineStatus; page?: number }) {
  return useQuery({
    queryKey: ['pipelines', params],
    queryFn: () => fetchPipelines(params),
    staleTime: 15_000,
  });
}

export function usePipeline(id: string | null) {
  return useQuery({
    queryKey: ['pipeline', id],
    queryFn: () => fetchPipeline(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const d = query.state.data;
      return d?.status === 'running' ? 3000 : false;
    },
  });
}

export function useCreatePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createPipeline,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipelines'] }),
  });
}

export function useExecutePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...rest }: { id: string; parameters?: Record<string, unknown>; dry_run?: boolean }) => executePipeline(id, rest),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipelines'] }),
  });
}

export function useDeletePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deletePipeline,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipelines'] }),
  });
}

// ── React Query Hooks — Agents ──

export function useLaunchInvestigation() {
  return useMutation({ mutationFn: launchInvestigation });
}

export function useLaunchExploration() {
  return useMutation({ mutationFn: launchExploration });
}

export function useGenerateHypotheses() {
  return useMutation({ mutationFn: generateHypotheses });
}

export function useGenerateInsights() {
  return useMutation({ mutationFn: generateInsights });
}

export function useAgentInvestigation(id: string | null) {
  return useQuery({
    queryKey: ['agent-investigation', id],
    queryFn: () => fetchAgentInvestigation(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const d = query.state.data;
      return d && (d.status === 'pending' || d.status === 'active') ? 3000 : false;
    },
  });
}

export function useDailyBrief() {
  return useQuery({
    queryKey: ['daily-brief'],
    queryFn: fetchDailyBrief,
    staleTime: 300_000,
    retry: 1,
  });
}

// ── React Query Hooks — OSINT Advanced ──

export function useOsintScrape() {
  return useMutation({ mutationFn: osintScrape });
}

export function useOsintCrawl() {
  return useMutation({ mutationFn: osintCrawl });
}

export function useOsintExtractEntities() {
  return useMutation({ mutationFn: osintExtractEntities });
}

export function useOsintThreats() {
  return useQuery({
    queryKey: ['osint-threats'],
    queryFn: osintThreats,
    staleTime: 30_000,
  });
}

export function useOsintThreatScan() {
  return useMutation({ mutationFn: osintThreatScan });
}

export function useOsintAgentInvestigatePerson() {
  return useMutation({ mutationFn: osintAgentInvestigatePerson });
}

export function useOsintAgentInvestigateTopic() {
  return useMutation({ mutationFn: osintAgentInvestigateTopic });
}

export function useOsintAgentSearch(query: string) {
  return useQuery({
    queryKey: ['osint-agent-search', query],
    queryFn: () => osintAgentSearch(query),
    enabled: query.length >= 2,
    staleTime: 10_000,
  });
}

export function useOsintAgentStats() {
  return useQuery({
    queryKey: ['osint-agent-stats'],
    queryFn: osintAgentStats,
    staleTime: 30_000,
  });
}

// ── React Query Hooks — Dark Web ──

export function useDarkwebSearch() {
  return useMutation({ mutationFn: darkwebSearch });
}

export function useDarkwebLeakCheck() {
  return useMutation({ mutationFn: darkwebLeakCheck });
}

export function useDarkwebAlerts() {
  return useQuery({
    queryKey: ['darkweb-alerts'],
    queryFn: darkwebAlerts,
    staleTime: 30_000,
  });
}

// ── React Query Hooks — Correlation ──

export function useRunCorrelation() {
  return useMutation({ mutationFn: runCorrelation });
}

export function useTemporalCorrelation() {
  return useMutation({ mutationFn: temporalCorrelation });
}

export function useLinkPrediction() {
  return useMutation({ mutationFn: linkPrediction });
}
